import { apiFetch } from './client';

/**
 * LOS ASUNTOS — la única fuente del panel de Athena.
 *
 * Hoy, Agenda y Me toca llaman todas acá. Esa es la razón de que no puedan
 * contradecirse: no hay otra fuente que consultar.
 */

export type Cajon = 'TRABAJO' | 'DINERO' | 'CLIENTES' | 'PERSONAL';
export type Prioridad = 'ALTA' | 'MEDIA' | 'BAJA' | 'RUIDO' | 'SIN_REVISAR';
export type Confianza = 'SEGURO' | 'DUDOSO';

export type EstadoDeAsunto =
  | 'DETECTADO'
  | 'APROBADO'
  | 'ASIGNADO'
  | 'POSPUESTO'
  | 'PROGRAMADO'
  | 'AVISADO'
  | 'RESPONDIDO'
  | 'CUMPLIDO'
  | 'VENCIDO'
  | 'DESCARTADO'
  /** Personal, esperando que el dueño diga si se guarda o se ignora. */
  | 'PREGUNTANDO'
  /** Dijo que no. Athena no lo vuelve a proponer ni a mencionar. */
  | 'IGNORADO';

export interface MiembroDeEquipo {
  id: string;
  nombre: string;
  rol: string | null;
  telefono: string | null;
  pendientes?: number;
  atrasados?: number;
}

export interface Asunto {
  id: string;
  codigo: string;
  tipo: string;
  descripcion: string;
  tema: string | null;
  cajon: Cajon;
  prioridad: Prioridad;
  /** Por qué tiene esa prioridad. Sin esto no se puede discutir. */
  porQue: string | null;
  confianza: Confianza;
  /** "No dijo la hora". Qué revisar, sin releer el chat. */
  porQueDudoso: string | null;
  depuradoAt: string | null;
  fechaCompromiso: string;
  fechaLimite: string | null;
  pospuestoHasta: string | null;
  estado: EstadoDeAsunto;
  origen: string;
  montoCentavos: number | null;
  moneda: string | null;
  /** El mensaje exacto del que salió. Todo se puede rastrear. */
  mensajeId: string | null;
  citaTextual: string | null;
  vistoEnChats: number;
  esDemo: boolean;
  historialJson: Array<{ cuando: string; quien: string; que: string; nota?: string }>;
  createdAt: string;
  conversationId: string;
  contactId: string;
  contact: { id: string; name: string | null; pushName: string | null; phone: string | null } | null;
  responsable: MiembroDeEquipo | null;
  conQuien: string;
  /** Lo decide el servidor: dos pestañas no pueden discrepar. */
  vencido: boolean;
  /** Todavía es una suposición de la máquina: sale punteado. */
  esSuposicion: boolean;
}

export interface FiltroDeAsuntos {
  estado?: string;
  cajon?: Cajon;
  /** Lo personal no sale salvo que se pida. */
  incluirPersonal?: boolean;
  incluirRuido?: boolean;
  desde?: string;
  hasta?: string;
  responsableId?: string;
  soloMios?: boolean;
  limite?: number;
}

export type AccionDeAsunto =
  | 'aprobar'
  | 'hecho'
  | 'descartar'
  | 'posponer'
  | 'asignar'
  | 'rescatar'
  /** "Sí, guardámelo." Queda en el cajón personal, que no sale en Hoy. */
  | 'guardar'
  /** "No." No se borra la fila: es lo que hace que no te lo vuelva a preguntar. */
  | 'ignorar';

export function getAsuntos(
  filtro: FiltroDeAsuntos = {},
): Promise<{ asuntos: Asunto[]; total: number }> {
  return apiFetch('/api/asuntos', { query: filtro });
}

/**
 * LO QUE ATHENA TE QUIERE PREGUNTAR.
 *
 * Hallazgos personales que NO entraron a ninguna lista de trabajo. Ruta aparte
 * y no un filtro: así lo personal sale solo por donde se lo pide a propósito.
 */
export function getLoQueTePregunto(): Promise<{ asuntos: Asunto[]; total: number }> {
  return apiFetch('/api/asuntos/te-pregunto');
}

export function getAsunto(id: string): Promise<Asunto> {
  return apiFetch(`/api/asuntos/${id}`);
}

export function moverAsunto(
  id: string,
  accion: AccionDeAsunto,
  datos: { hasta?: string; responsableId?: string; fechaLimite?: string; nota?: string } = {},
): Promise<Asunto> {
  return apiFetch(`/api/asuntos/${id}/accion`, { method: 'POST', body: { accion, ...datos } });
}

export function corregirAsunto(
  id: string,
  campos: {
    fechaCompromiso?: string;
    montoCentavos?: number | null;
    moneda?: string | null;
    cajon?: Cajon;
    descripcion?: string;
  },
): Promise<Asunto> {
  return apiFetch(`/api/asuntos/${id}`, { method: 'PATCH', body: campos });
}

export function getEquipo(): Promise<{ miembros: MiembroDeEquipo[] }> {
  return apiFetch('/api/equipo');
}

/** Lo que hace falta para crear un asunto a mano. */
export interface AsuntoNuevo {
  descripcion: string;
  /** ISO. Cuándo toca. */
  fechaCompromiso: string;
  frenteId?: string | null;
  tipo?: string;
  prioridad?: 'ALTA' | 'MEDIA' | 'BAJA';
  montoCentavos?: number | null;
  moneda?: string | null;
  contactId?: string | null;
}

/** Nace APROBADO: lo que escribe el dueño no es una suposición que confirmar. */
export function crearAsunto(datos: AsuntoNuevo): Promise<Asunto> {
  return apiFetch('/api/asuntos', { method: 'POST', body: datos });
}
